import { useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth, signOutUser } from '../../pages/Authentication/AuthContext';

// Read timeout from environment variable, default to 120 minutes (2 hours).
const IDLE_TIMEOUT_MINUTES = import.meta.env.VITE_IDLE_TIMEOUT_MINUTES || 120;
const IDLE_TIMEOUT_MS = IDLE_TIMEOUT_MINUTES * 60 * 1000;

const IdleSignOut = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Use a ref to hold the timer ID. This prevents issues with stale closures
  // and ensures we are always clearing the correct timer.
  const idleTimerRef = useRef(null);

  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    if (currentUser) {
      idleTimerRef.current = setTimeout(async () => {
        console.log(
          `User has been idle for ${IDLE_TIMEOUT_MINUTES} minutes. Signing out.`
        );
        await signOutUser();
        // Navigate to a page confirming sign-out
        navigate('/auth/signon?reason=idle');
      }, IDLE_TIMEOUT_MS);
    }
  }, [currentUser, navigate]);

  // Reset timer on user activity
  useEffect(() => {
    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart'];
    const reset = () => resetIdleTimer();

    events.forEach(event => window.addEventListener(event, reset));
    resetIdleTimer();

    return () => {
      events.forEach(event => window.removeEventListener(event, reset));
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    };
  }, [resetIdleTimer]);

  // Reset timer on page navigation
  useEffect(() => {
    resetIdleTimer();
  }, [location, resetIdleTimer]);

  return null; // This component does not render anything
};

export default IdleSignOut;
